import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  TextInput,
  Select,
  SimpleGrid,
  FileInput,
  Checkbox,
  NumberInput,
} from "@mantine/core";
import DocumentFilePicker from "../../../../common/components/DocumentFilePicker/DocumentFilePicker";
import { DatePickerInput } from "@mantine/dates";
import {
  NOMINEE_RELATION_OPTIONS,
  normalizeName,
  validateName,
  validatePhone,
  validateAadhaar,
} from "../../../../utils/issuance";

const nameRules = (label) => ({
  required: `${label} is required`,
  validate: (v) => validateName(v, { label }),
});

const nameFieldProps = (field) => ({
  ...field,
  onBlur: (e) => {
    field.onChange(normalizeName(e.target.value));
    field.onBlur();
  },
});

const TravelerInfoForm = forwardRef(
  ({ onSubmit, travellerIndex, formData, getRequiredDocs, options, compact = false }, ref) => {
    const { handleSubmit, watch, control, setValue } = useForm({
      mode: "onChange",
      reValidateMode: "onChange",
      defaultValues: {
        indian_resident: false,
        is_primary: travellerIndex === 0 ? true : false,
        nationality: "",
        first_name: "",
        last_name: "",
        email: "",
        phone_number: "",
        gender: "",
        dob: null,
        nominee_name: "",
        nominee_relation: "",
        passport_number: "",
        emergency_name: "",
        emergency_contact: "",
        emergency_email: "",
        residence_proof: null,
        passport_front: null,
        passport_back: null,
        aadhar_card_front: null,
        aadhar_card_back: null,
        flight_ticket: null,
      },
    });

    const isDomestic =
      formData.quote_data.nationality === "Indian" &&
      formData.quote_data.travelling_to === "India";

    const nationality = watch("nationality");

    const [requiredDocuments, setRequiredDocuments] = useState([]);

    const handleTravelerSubmit = (data) => {
      onSubmit(data, travellerIndex);
    };

    useEffect(() => {
      const tripNationality = formData.quote_data?.nationality;
      if (!tripNationality || !options?.length) return;
      const match = options.find((o) => o.label === tripNationality);
      if (match) setValue("nationality", match.value);
    }, [formData.quote_data?.nationality, options, setValue]);

    useEffect(() => {
      setValue(
        "indian_resident",
        formData.quote_data.residence_status === "yes"
      );
    }, [formData.quote_data.residence_status, setValue]);

    useEffect(() => {
      if (nationality) {
        const docs = getRequiredDocs(
          nationality,
          formData.quote_data.travelling_from,
          formData.quote_data.travelling_to,
          formData.quote_data.residence_status
        );
        setRequiredDocuments(docs);
      }
    }, [
      nationality,
      formData.quote_data.travelling_from,
      formData.quote_data.travelling_to,
      formData.quote_data.residence_status,
      getRequiredDocs,
    ]);

    useImperativeHandle(ref, () => ({
      submit: () =>
        new Promise((resolve) => {
          handleSubmit(
            (data) => {
              handleTravelerSubmit(data);
              resolve(true);
            },
            () => resolve(false)
          )();
        }),
    }));

    return (
      <form>
        <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing={compact ? "md" : "lg"}>
          <div style={{ display: "none" }}>
            <Controller
              name="indian_resident"
              control={control}
              render={({ field }) => (
                <Checkbox
                  {...field}
                  checked={field.value}
                  onChange={(event) =>
                    field.onChange(event.currentTarget.checked)
                  }
                />
              )}
            />
          </div>

          <div style={{ display: "none" }}>
            <Controller
              name="is_primary"
              control={control}
              render={({ field }) => (
                <Checkbox
                  {...field}
                  checked={field.value}
                  onChange={(event) =>
                    field.onChange(event.currentTarget.checked)
                  }
                />
              )}
            />
          </div>

          <Controller
            name="nationality"
            control={control}
            rules={{ required: "Nationality is required" }}
            render={({ field, fieldState: { error } }) => (
              <Select
                label="Nationality"
                placeholder="Nationality"
                data={options}
                value={field.value}
                onChange={field.onChange}
                searchable
                error={error?.message}
                withAsterisk
              />
            )}
          />

          <Controller
            name="first_name"
            control={control}
            rules={nameRules("First name")}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...nameFieldProps(field)}
                label="First Name"
                placeholder="First Name"
                error={error?.message}
                withAsterisk
              />
            )}
          />

          <Controller
            name="last_name"
            control={control}
            rules={nameRules("Last name")}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...nameFieldProps(field)}
                label="Last Name"
                placeholder="Last Name"
                error={error?.message}
                withAsterisk
              />
            )}
          />

          <Controller
            name="email"
            control={control}
            rules={{ required: "Email is required" }}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                label="Email"
                type="email"
                placeholder="Email"
                error={error?.message}
                withAsterisk
              />
            )}
          />

          <Controller
            name="phone_number"
            control={control}
            rules={{
              required: "Phone number is required",
              validate: (v) => validatePhone(v, { label: "Phone number" }),
            }}
            render={({ field, fieldState: { error } }) => (
              <NumberInput
                label="Phone number"
                placeholder="Phone Number"
                hideControls
                allowDecimal={false}
                allowNegative={false}
                clampBehavior="none"
                error={error?.message}
                withAsterisk
                {...field}
              />
            )}
          />

          <Controller
            name="gender"
            control={control}
            rules={{ required: "Gender is required" }}
            render={({ field, fieldState: { error } }) => (
              <Select
                label="Gender"
                placeholder="Select gender"
                data={[
                  { value: "1", label: "Male" },
                  { value: "2", label: "Female" },
                  { value: "3", label: "Other" },
                ]}
                value={field.value}
                onChange={field.onChange}
                error={error?.message}
                withAsterisk
              />
            )}
          />

          <Controller
            name="dob"
            control={control}
            rules={{
              required: "DOB is required",
              validate: (value) => {
                if (!value) return true;
                const date = value instanceof Date ? value : new Date(value);
                if (Number.isNaN(date.getTime())) return "Enter a valid date";
                const today = new Date();
                today.setHours(23, 59, 59, 999);
                return date <= today || "Date of birth cannot be after today";
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <DatePickerInput
                label="Date of birth"
                placeholder="traveller dob"
                valueFormat="YYYY MMM DD"
                maxDate={new Date()}
                {...field}
                error={error?.message}
                withAsterisk
              />
            )}
          />

          <Controller
            name="nominee_name"
            control={control}
            rules={nameRules("Nominee name")}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...nameFieldProps(field)}
                label="Nominee Name"
                placeholder="Nominee Name"
                error={error?.message}
                withAsterisk
              />
            )}
          />

          <Controller
            name="nominee_relation"
            control={control}
            rules={{ required: "nominee relation is required" }}
            render={({ field, fieldState: { error } }) => (
              <Select
                label="Nominee Relation"
                placeholder="Select your relation with nominee"
                data={NOMINEE_RELATION_OPTIONS}
                value={field.value}
                onChange={field.onChange}
                error={error?.message}
                withAsterisk
                searchable
              />
            )}
          />

          <Controller
            name="passport_number"
            control={control}
            rules={{
              validate: (v) =>
                isDomestic ? validateAadhaar(v) : true,
            }}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                label={isDomestic ? "Aadhar Number" : "Passport Number"}
                placeholder={isDomestic ? "Aadhar Number" : "Passport Number"}
                error={error?.message}
                onBlur={(e) => {
                  field.onChange(e.target.value.replace(/\s/g, ""));
                  field.onBlur();
                }}
              />
            )}
          />

          <Controller
            name="emergency_name"
            control={control}
            rules={{
              validate: (v) =>
                validateName(v, { required: false, label: "Emergency name" }),
            }}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...nameFieldProps(field)}
                label="Emergency Name"
                placeholder="Emergency Contact Name"
                error={error?.message}
              />
            )}
          />

          <Controller
            name="emergency_contact"
            control={control}
            rules={{
              validate: (v) =>
                validatePhone(v, { required: false, label: "Emergency contact" }),
            }}
            render={({ field, fieldState: { error } }) => (
              <NumberInput
                label="Emergency Contact"
                placeholder="Emergency Contact Number"
                hideControls
                allowDecimal={false}
                allowNegative={false}
                clampBehavior="none"
                error={error?.message}
                {...field}
              />
            )}
          />

          <Controller
            name="emergency_email"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Emergency Email"
                type="email"
                placeholder="Emergency Contact Email"
              />
            )}
          />

          {requiredDocuments.includes("residence_proof") && (
            <Controller
              name="residence_proof"
              control={control}
              rules={{ required: "Residence Proof is required" }}
              render={({ field, fieldState: { error } }) => (
                <FileInput
                  label="Residence Proof"
                  placeholder="Residence Proof"
                  onChange={field.onChange}
                  error={error?.message}
                />
              )}
            />
          )}

          {requiredDocuments.includes("passport_front") && (
            <Controller
              name="passport_front"
              control={control}
              rules={{ required: "Passport front is required" }}
              render={({ field, fieldState: { error } }) => (
                <DocumentFilePicker
                  label="Passport Front"
                  value={field.value}
                  onChange={field.onChange}
                  error={error?.message}
                />
              )}
            />
          )}

          {requiredDocuments.includes("passport_back") && (
            <Controller
              name="passport_back"
              control={control}
              rules={{ required: "Passport back is required" }}
              render={({ field, fieldState: { error } }) => (
                <DocumentFilePicker
                  label="Passport Back"
                  value={field.value}
                  onChange={field.onChange}
                  error={error?.message}
                />
              )}
            />
          )}

          {requiredDocuments.includes("aadhar_card_front") && (
            <Controller
              name="aadhar_card_front"
              control={control}
              rules={{ required: "Aadhar card front is required" }}
              render={({ field, fieldState: { error } }) => (
                <FileInput
                  label="Aadhar Card Front"
                  placeholder="Aadhar Card Front"
                  onChange={field.onChange}
                  error={error?.message}
                />
              )}
            />
          )}

          {requiredDocuments.includes("aadhar_card_back") && (
            <Controller
              name="aadhar_card_back"
              control={control}
              rules={{ required: "Aadhar card back is required" }}
              render={({ field, fieldState: { error } }) => (
                <FileInput
                  label="Aadhar Card Back"
                  placeholder="Aadhar Card Back"
                  onChange={field.onChange}
                  error={error?.message}
                />
              )}
            />
          )}

          {requiredDocuments.includes("flight_ticket") && (
            <Controller
              name="flight_ticket"
              control={control}
              rules={{ required: "Flight ticket back is required" }}
              render={({ field, fieldState: { error } }) => (
                <FileInput
                  label="Flight Ticket"
                  placeholder="Flight Ticket"
                  onChange={field.onChange}
                  error={error?.message}
                />
              )}
            />
          )}
        </SimpleGrid>
      </form>
    );
  }
);

TravelerInfoForm.displayName = "TravelerInfoForm";

export default TravelerInfoForm;
